import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import 'react-dates/initialize';
import classnames from 'clsx';
import moment from 'moment';
import { defineMessages, FormattedMessage } from 'react-intl';

import { DropdownMenuPosition } from 'ui-components/components/INTERNAL/dropdown/dropdown-menu-position';

import { MM_DD_YYYY } from '../../../constants/date-format';
import { twelveHourUserSettingsNoTimeZoneUnpadded } from '../../../constants/timezone-format';
import { Button } from '../button';
import { DatePickerDropdown } from '../date-picker-dropdown';
import { DateTimePickerDropdown } from '../date-time-picker-dropdown';
import { MDash } from '../mdash/mdash';

import '../../styles/lib-overrides/react-dates.css';
import style from './link-date-picker.module.less';

/**
 * @typedef {import('ui-components/components/dropdown/dropdown-types.d.ts').DropdownMenuPositions} DropdownMenuPositions
 */

/**
 * @typedef {import('../../../prop-types/intl-types.d.ts').MessageDescriptor} MessageDescriptor
 */

/**
 * @typedef {import('../date-picker-dropdown/date-picker-dropdown-types.d.ts').DateChangeHandler} DateChangeHandler
 */
/**
 * @typedef {import('../date-picker-dropdown/date-picker-dropdown-types.d.ts').IsOutsideRangeFunction} IsOutsideRangeFunction
 */

// TODO: take out localized messages from ui-components when there is a better solution.
const messages = defineMessages({
  today: {
    description: 'linkDatePicker.today',
    defaultMessage: 'Today',
    id: 'LYIe9z',
  },
});

const getStartOfTodayUtc = () => moment().utc().startOf('day');

const getIsToday = (value, sellerTimeZone) => {
  const momentDate = moment.tz(value, sellerTimeZone);
  return momentDate.format() === getStartOfTodayUtc().tz(sellerTimeZone, true).format();
};

const getDisplayInfo = (value, dateFormat, displayMessageDescriptor, shouldUseDateTimePicker, sellerTimeZone, buttonText) => {
  const momentSelectedDate = !value ? moment() : moment(value, dateFormat);
  const formattedDate = momentSelectedDate.format(dateFormat);
  const isToday = shouldUseDateTimePicker ? getIsToday(value, sellerTimeZone) : formattedDate === moment().format(dateFormat);

  let displayText;
  if (displayMessageDescriptor) {
    displayText = (
      <FormattedMessage
        {...displayMessageDescriptor}
        values={{
          date: value,
          isToday,
        }}
      />
    );
  } else if (isToday) {
    displayText = <FormattedMessage {...messages.today} />;
  } else if (shouldUseDateTimePicker) {
    displayText = buttonText;
  } else {
    displayText = formattedDate;
  }

  return {
    displayText,
    momentSelectedDate: formattedDate,
  };
};

/**
 * @param {object} props
 * @param {boolean} [props.allowPastDates]
 * @param {DropdownMenuPositions} [props.calendarPosition]
 * @param {string} [props.className]
 * @param {string} [props.dateFormat]
 * @param {boolean} [props.disabled]
 * @param {MessageDescriptor} [props.displayMessageDescriptor]
 * @param {string} [props.dropdownIconClassName]
 * @param {MessageDescriptor} [props.emptyStateMessageDescriptor]
 * @param {string} [props.id]
 * @param {IsOutsideRangeFunction} [props.isOutsideRange]
 * @param {string} [props.linkButtonClassName]
 * @param {DateChangeHandler} props.onChange
 * @param {boolean} [props.showEmptyState]
 * @param {string} [props.value] - this can be an iso string "2024-11-05T06:00:00.000Z" or formatted date "11/23/2024"
 * @param {boolean} [props.shouldUseDateTimePicker]
 * @param {string} [props.buttonText]
 * @param {string} [props.timeFormat]
 * @param {string} [props.sellerTimeZone]
 * @returns {JSX.Element}
 */
export const LinkDatePicker = ({
  allowPastDates = false,
  calendarPosition = DropdownMenuPosition.BottomLeft,
  className = '',
  dateFormat = MM_DD_YYYY,
  disabled = false,
  displayMessageDescriptor,
  dropdownIconClassName,
  emptyStateMessageDescriptor,
  id,
  isOutsideRange = null,
  linkButtonClassName = style['link-button'],
  onChange,
  showEmptyState = false,
  value, // NOTE: this is can be a iso string "2024-11-05T06:00:00.000Z" or formatted date "11/23/2024"
  shouldUseDateTimePicker = false,
  buttonText = '',
  timeFormat = twelveHourUserSettingsNoTimeZoneUnpadded,
  sellerTimeZone,
}) => {
  const displayInfo = getDisplayInfo(value, dateFormat, displayMessageDescriptor, shouldUseDateTimePicker, sellerTimeZone, buttonText);
  const shouldDisplayEmptyState = showEmptyState && !value;
  const emptyStateDisplay = emptyStateMessageDescriptor ? <FormattedMessage {...emptyStateMessageDescriptor} /> : <MDash />;

  const button = (
    <Button className={linkButtonClassName} disabled={disabled} theme='link'>
      {shouldDisplayEmptyState ? emptyStateDisplay : displayInfo.displayText}
      <div className={classnames(style['icon-wrapper'], dropdownIconClassName)}>
        <FontAwesomeIcon icon={faCaretDown} />
      </div>
    </Button>
  );

  if (shouldUseDateTimePicker) {
    return (
      <div className={classnames(style['date-picker-wrapper'], className)}>
        <DateTimePickerDropdown
          allowPastDates={allowPastDates}
          calendarPosition={calendarPosition}
          dateFormat={dateFormat}
          id={id}
          onChange={onChange}
          openTogglerClassName={style.toggled}
          sellerTimeZone={sellerTimeZone}
          timeFormat={timeFormat}
          value={value}
        >
          {button}
        </DateTimePickerDropdown>
      </div>
    );
  }

  return (
    <div className={classnames(style['date-picker-wrapper'], className)}>
      <DatePickerDropdown
        allowPastDates={allowPastDates}
        calendarPosition={calendarPosition}
        dateFormat={dateFormat}
        id={id}
        isOutsideRange={isOutsideRange}
        onChange={onChange}
        openTogglerClassName={style.toggled}
        value={displayInfo.momentSelectedDate}
      >
        {button}
      </DatePickerDropdown>
    </div>
  );
};
